import { c as _c } from "react/compiler-runtime";
import { APIProvider, ControlPosition, Map, MapCameraChangedEvent, MapControl } from '@vis.gl/react-google-maps';
import debounce from 'lodash.debounce';
import React from 'react';
import axios from 'shared/anaxios';
import { Box } from 'shared/grid';
import useComponentSize from 'shared/hooks/component-size';
import { MapAutocomplete } from './autocomplete';
const PIN_PATH = `M20.2,15.7L20.2,15.7c1.1-1.6,1.8-3.6,1.8-5.7c0-5.6-4.5-10-10-10S2,4.5,2,10c0,2,0.6,3.9,1.6,5.4c0,0.1,0.1,0.2,0.2,0.3
  c0,0,0.1,0.1,0.1,0.2c0.2,0.3,0.4,0.6,0.7,0.9c2.6,3.1,7.4,7.6,7.4,7.6s4.8-4.5,7.4-7.5c0.2-0.3,0.5-0.6,0.7-0.9
  C20.1,15.8,20.2,15.8,20.2,15.7z`;
const PinMarker = t0 => {
  const $ = _c(7);
  const {
    size: t1,
    color
  } = t0;
  const size = t1 === undefined ? 20 : t1;
  const t2 = `translate(${-size / 2}px,${-size}px)`;
  let t3;
  if ($[0] !== color || $[1] !== t2) {
    t3 = {
      pointerEvents: "none",
      cursor: "pointer",
      stroke: "#fff",
      strokeWidth: 2,
      fill: color,
      transform: t2
    };
    $[0] = color;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <path d={PIN_PATH} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== size || $[5] !== t3) {
    t5 = <svg height={size} viewBox="0 0 24 24" style={t3}>{t4}</svg>;
    $[4] = size;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
const geocodeToFormItems = item => {
  const location = item.address_components.reduce((agg, val) => {
    if (val.types.includes('street_number')) {
      agg['street_number'] = val.long_name;
    }
    if (val.types.includes('route')) {
      agg['street_name'] = val.long_name;
    }
    if (val.types.includes('sublocality_level_2')) {
      agg['suburb'] = val.long_name;
    }
    if (val.types.includes('locality')) {
      agg['city'] = val.long_name;
    }
    if (val.types.includes('administrative_area_level_1')) {
      agg['province'] = val.long_name;
    }
    if (val.types.includes('country')) {
      agg['country'] = val.short_name;
    }
    if (val.types.includes('postal_code')) {
      agg['postal_code'] = val.short_name;
    }
    return agg;
  }, {
    street_number: '',
    street_name: '',
    suburb: '',
    city: '',
    province: '',
    country: '',
    postal_code: ''
    // gps: [item.geometry.location.lat, item.geometry.location.lng],
  });
  const {
    street_number,
    street_name,
    suburb,
    ...rest
  } = location;
  return {
    street_address: [[street_number, street_name].filter(v => v).join(' '), suburb].filter(v => v).join('\n'),
    ...rest
  };
};
const placeToFormItems = place => {
  const location = place.addressComponents.reduce((agg, val) => {
    if (val.types.includes('street_number')) {
      agg['street_number'] = val.longText;
    }
    if (val.types.includes('route')) {
      agg['street_name'] = val.longText;
    }
    if (val.types.includes('sublocality_level_2')) {
      agg['suburb'] = val.longText;
    }
    if (val.types.includes('locality')) {
      agg['city'] = val.longText;
    }
    if (val.types.includes('administrative_area_level_1')) {
      agg['province'] = val.longText;
    }
    if (val.types.includes('country')) {
      agg['country'] = val.shortText;
    }
    if (val.types.includes('postal_code')) {
      agg['postal_code'] = val.shortText;
    }
    return agg;
  }, {
    street_number: '',
    street_name: '',
    suburb: '',
    city: '',
    province: '',
    country: '',
    postal_code: '',
    gps: [place.location.lat(), place.location.lng()]
  });
  const {
    street_number,
    street_name,
    suburb,
    ...rest
  } = location;
  return {
    street_address: [[street_number, street_name].filter(v => v).join(' '), suburb].filter(v => v).join('\n'),
    ...rest
  };
};
const geocode = debounce(({
  lat,
  lng
}, callback) => {
  return axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
    params: {
      // proximity: `${state.geoip.longitude},${state.geoip.latitude}`,
      latlng: `${lat},${lng}`,
      key: __GOOGLE_MAPS_KEY__
    }
  }).then(({
    data
  }) => {
    const results = data.results.map(r => ({
      ...r,
      text: r.formatted_address
    }));
    if (results.length) {
      const parts = geocodeToFormItems(results[0]);
      callback(parts);
    } else {
      callback({});
    }
  });
}, 1000);
export const LocationMap = ({
  defaultCenter = [0, 0],
  center,
  setCenter,
  onGeoCode
}: {
  defaultCenter?: [number, number];
  center: [number, number];
  setCenter: (center: [number, number]) => void;
  onGeoCode: (parts: Record<string, any>) => void;
}) => {
  const initialCenter = React.useMemo(() => {
    if (center && center.length === 2) {
      return {
        lat: center[0],
        lng: center[1]
      };
    }
    return {
      lat: 0,
      lng: 0
    };
  }, [center]);
  const onCameraChanged = React.useCallback((e: MapCameraChangedEvent) => {
    const {
      lat,
      lng
    } = e.detail.center;
    setCenter([lat, lng]);
    if (lat && lng && Math.abs(center[0] - lat) > 0.0001 && Math.abs(center[1] - lng) > 0.0001 && Math.abs(initialCenter.lat - lat) > 0.0001 && Math.abs(initialCenter.lng - lng) > 0.0001) {
      geocode({
        lat,
        lng
      }, ({
        gps,
        ...place
      }) => onGeoCode(place));
    }
  }, [center, initialCenter.lat, initialCenter.lng, onGeoCode, setCenter]);
  const onPlacesChanged = React.useCallback((place_0: google.maps.places.Place | null) => {
    const parts = placeToFormItems(place_0);
    onGeoCode(parts);
  }, [onGeoCode]);
  const mapWrapperRef = React.useRef<HTMLDivElement>(null);
  const {
    width: mapWidth
  } = useComponentSize(mapWrapperRef);
  return <Box ref={mapWrapperRef} sx={{
    // height: '500px',
    width: '100%',
    position: 'relative',
    zIndex: 1
  }} data-sentry-element="Box" data-sentry-component="LocationMap" data-sentry-source-file="index.tsx">
      <Box sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      zIndex: 1
    }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <PinMarker color="#dc4a3d" size={30} data-sentry-element="PinMarker" data-sentry-source-file="index.tsx" />
      </Box>

      <APIProvider apiKey={__GOOGLE_MAPS_KEY__} data-sentry-element="APIProvider" data-sentry-source-file="index.tsx">
        <Map style={{
        width: '100%',
        height: '600px'
      }}
      // defaultCenter={{lat: defaultLongitude, lng: defaultLongitude}}
      center={{
        lat: center[0] || defaultCenter[0],
        lng: center[1] || defaultCenter[1]
      }} defaultZoom={13} zoomControl={true}
      // gestureHandling={'greedy'}
      gestureHandling="cooperative" disableDefaultUI={true} onCameraChanged={onCameraChanged} data-sentry-element="Map" data-sentry-source-file="index.tsx">
          <MapControl position={ControlPosition.LEFT_TOP} data-sentry-element="MapControl" data-sentry-source-file="index.tsx">
            <Box p={4} sx={{
            zIndex: 1,
            width: mapWidth
          }} data-sentry-element="Box" data-sentry-source-file="index.tsx">
              <MapAutocomplete onPlaceSelect={onPlacesChanged} data-sentry-element="MapAutocomplete" data-sentry-source-file="index.tsx" />
            </Box>
          </MapControl>
        </Map>
      </APIProvider>
    </Box>;
};